import React from 'react';
import { motion } from 'framer-motion';

const MessageItem = ({ msg, character }) => {
  const isUser = msg.role === 'user';

  return (
    <motion.div
      initial={{ opacity: 0, y: 15, filter: 'blur(4px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={`flex w-full mb-8 md:mb-12 ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div className={`w-full flex ${isUser ? 'justify-end max-w-[95%] sm:max-w-[85%] md:max-w-[75%]' : 'justify-start max-w-[95%] sm:max-w-[85%] md:max-w-[75%] gap-3 md:gap-6'}`}>
        {/* 對方的發言 */}
        {!isUser && (
          <>
            <div className="w-8 h-8 md:w-12 md:h-12 rounded-lg md:rounded-xl overflow-hidden border border-amber-500/20 flex-shrink-0 opacity-90 shadow-[0_4px_20px_rgba(0,0,0,0.6)] mt-1 md:mt-0">
              <img
                src={character.img}
                alt={character.name}
                className="w-full h-full object-cover filter contrast-125 sepia-[0.3] hue-rotate-[-10deg]"
              />
            </div>
            <div className="flex flex-col items-start pt-1">
              <span className="text-[8px] md:text-[9px] tracking-[0.3em] text-amber-400/80 uppercase mb-1.5 md:mb-2 drop-shadow-sm">
                {character.role}
              </span>
              <div className="text-left text-white font-serif text-[15px] sm:text-[16px] md:text-[18px] leading-[2] md:leading-[2.2] tracking-wide md:tracking-wider relative group drop-shadow-md break-words whitespace-pre-wrap">
                <span className="absolute -left-4 md:-left-6 top-0 text-amber-500/50 font-serif text-xl md:text-2xl select-none transition-colors group-hover:text-amber-400">「</span>
                <span className="relative z-10 px-1">{msg.content}</span>
                <span className="absolute -right-4 md:-right-6 bottom-0 text-amber-500/50 font-serif text-xl md:text-2xl select-none transition-colors group-hover:text-amber-400">」</span>
              </div>
            </div>
          </>
        )}

        {/* 您的發言 */}
        {isUser && (
          <div className="flex flex-col items-end w-full">
            <span className="text-[8px] md:text-[9px] tracking-[0.3em] text-gray-400 uppercase mb-1.5 md:mb-2 ml-auto">
              You
            </span>
            <div className="flex flex-col items-end gap-3 w-full">
              {/* 如果是三張牌陣,特殊渲染 */}
              {msg.card?.isThreeCardSpread && (
                <div className="mb-2 bg-gradient-to-br from-[#160c08] to-[#0a0a0c] border border-amber-500/20 rounded-xl p-4 md:p-5 w-full max-w-md shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
                  <h4 className="text-amber-400 text-xs md:text-sm tracking-widest mb-2 text-center font-serif">
                    {msg.card.spreadType.name}
                  </h4>
                  <p className="text-amber-400/60 text-[9px] tracking-wider mb-4 text-center">
                    {msg.card.spreadType.subtitle}
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {msg.card.threeCards.map((card, i) => (
                      <div key={i} className="flex flex-col items-center">
                        <div className="w-full aspect-[2/3] rounded-md overflow-hidden border border-amber-500/30 shadow-inner mb-2">
                          <img
                            src={card.img}
                            alt={card.name}
                            className={`w-full h-full object-cover ${!card.isUpright ? 'rotate-180' : ''}`}
                          />
                        </div>
                        <span className="text-amber-400/70 text-[8px] tracking-wider uppercase mb-1">
                          {msg.card.spreadType.positions[i].title}
                        </span>
                        <span className="text-amber-50 text-[10px] md:text-xs font-serif text-center">
                          {card.name}
                        </span>
                        <span className="text-amber-200/60 text-[8px] mt-0.5">{card.statusText}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 如果是單張牌,原有的卡片渲染 */}
              {msg.card && !msg.card.isThreeCardSpread && (
                <div className="mb-4 bg-gradient-to-br from-[#160c08] to-[#0a0a0c] border border-amber-500/20 rounded-xl p-3 md:p-4 flex items-center gap-4 shadow-[0_8px_30px_rgba(0,0,0,0.5)] w-full max-w-[280px] md:max-w-xs">
                  <div className="w-14 h-20 md:w-16 md:h-24 flex-shrink-0 rounded-md overflow-hidden border border-amber-500/30 relative shadow-inner">
                    <img
                      src={msg.card.img}
                      alt={msg.card.name}
                      className={`w-full h-full object-cover ${!msg.card.isUpright ? 'rotate-180' : ''}`}
                    />
                  </div>
                  <div className="flex flex-col text-left justify-center flex-1">
                    <span className="text-amber-500/70 text-[9px] md:text-[10px] tracking-widest uppercase mb-1">
                      {msg.card.nameEn}
                    </span>
                    <h4 className="text-amber-50 text-sm md:text-base font-serif tracking-widest mb-2">
                      {msg.card.name}
                      <span className="text-xs font-sans text-amber-200/80 ml-1 opacity-80">{msg.card.statusText}</span>
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {msg.card.currentMeaning.keywords.slice(0, 3).map((kw, idx) => (
                        <span
                          key={idx}
                          className="text-[8px] md:text-[9px] text-amber-200/60 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/10 whitespace-nowrap"
                        >
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* 一般文字訊息 */}
              <div className="text-right text-amber-50/90 font-light text-[14px] md:text-[15px] leading-relaxed tracking-wide bg-gradient-to-bl from-amber-900/40 to-[#0a0a0c]/90 border border-amber-500/20 rounded-2xl rounded-tr-sm px-4 py-3 md:px-6 md:py-4 shadow-[0_4px_20px_rgba(245,158,11,0.05)] backdrop-blur-md break-words inline-block max-w-full whitespace-pre-wrap">
                {msg.content}
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default MessageItem;